import React, { useState, forwardRef, useImperativeHandle, useEffect } from "react";
import TableContainer from "Common/TableContainer";
import { useFormik } from "formik";
import "flatpickr/dist/flatpickr.css";
import Select from "react-select";
import * as Yup from "yup";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import { useLoader } from "../../../context/LoaderContext";
import {
  addPatrimonialPersonalReferences,
  getPatrimonialPersonalReferencesByPatrimonialStateId,
} from "helpers/fakebackend_helper";

const PatrimonialPersonalReference = forwardRef(
  (
    {
      onSuccess,
      patrimonialStateId,
    }: { onSuccess?: () => void; patrimonialStateId?: string },
    ref
  ) => {
    const [showPatrimonialPersonalForm, setShowPatrimonialPersonalForm] = useState(false);
    const [patrimonialPersonalList, setPatrimonialPersonalList] = useState<any[]>([]);
    const { showLoader, hideLoader } = useLoader();

    const handleAddNewPatrimonialPersonal = () => {
      setShowPatrimonialPersonalForm(true);
    };

    const personalReferenceOptions = [
      { value: "Familiar", label: "Familiar" },
      { value: "Padre", label: "Padre" },
      { value: "Madre", label: "Madre" },
      { value: "Hermano(a)", label: "Hermano(a)" },
      { value: "Tio(a)", label: "Tio(a)" },
      { value: "Primo(a)", label: "Primo(a)" },
      { value: "Amigo", label: "Amigo" },
      { value: "Compañero de trabajo", label: "Compañero de trabajo" },
      { value: "Vecino", label: "Vecino" },
      { value: "Otro", label: "Otro (Especificar en comentario)" },
    ];

    const handleBackToList = () => {
      setShowPatrimonialPersonalForm(false);
    };

    useImperativeHandle(ref, () => ({
      submitForm: () => {
        validation.handleSubmit();
      },
      resetForm: () => {
        validation.resetForm();
        setPatrimonialPersonalList([]);
      },
    }));

    /**
     * Fetch PatrimonialPersonals by patrimonialStateId
     */
    const fetchPatrimonialPersonals = async () => {
      if (!patrimonialStateId) return;

      const params = {patrimonialStateId}

      showLoader();
      try {
        const response = await getPatrimonialPersonalReferencesByPatrimonialStateId(params);
        const patrimonialPersonalReferences = response.data._embedded.patrimonialPersonalReferences || [];
        setPatrimonialPersonalList(patrimonialPersonalReferences);
      } catch (error) {
        toast.error("Error al obtener referencias personales: " + error);
      } finally {
        hideLoader();
      }
    };

    /**
     * Effect to fetch assets when patrimonialStateId changes
     */
    useEffect(() => {
      fetchPatrimonialPersonals();
    }, [patrimonialStateId]);

    const linkifyEntity = (entityName: string, id: number) => {
      return `/${entityName}s/${id}`;
    };

    const validation: any = useFormik({
      enableReinitialize: true,
      initialValues: {
        names: "",
        surnames: "",
        email: "",
        phoneNumber: "",
        address: "",
        typePersonalReference: "",
        comments: "",
      },
      validationSchema: Yup.object({
        names: Yup.string().required("Nombres es requerido"),
        surnames: Yup.string().required("Apellidos es requerido"),
        phoneNumber: Yup.string().required("Teléfono es requerido"),
        address: Yup.string().required("Dirección es requerida"),
        typePersonalReference: Yup.string().required("Tipo de referencia es requerido"),
      }),
      onSubmit: async (values: any) => {
        if (!patrimonialStateId) {
          toast.error("Error: No se ha proporcionado el ID del estado patrimonial");
          return;
        }

        try {
          const assetData = {
            patrimonialStatus: linkifyEntity("patrimonialStatus", Number(patrimonialStateId)),
            names: values.names,
            surnames: values.surnames,
            email: values.email,
            phoneNumber: values.phoneNumber,
            address: values.address,
            typePersonalReference: values.typePersonalReference,
            comments: values.comments,
          };

          const response = await addPatrimonialPersonalReferences(assetData);
          if (!response || !response.data) {
            toast.error("Error al agregar referencia personal");
            return;
          }

          toast.success("Referencia personal agregada correctamente");
          // Refresh the asset list after adding
          fetchPatrimonialPersonals();

          setShowPatrimonialPersonalForm(false);
          validation.resetForm();

          onSuccess?.();
        } catch (error) {
          toast.error("Error al agregar referencia personal: " + error);
        }
      },
    });

    const columns = [
      {
        header: "Nombres",
        accessorKey: "names",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Apellidos",
        accessorKey: "surnames",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Email",
        accessorKey: "email",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Teléfono",
        accessorKey: "phoneNumber",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Dirección",
        accessorKey: "address",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Tipo de Referencia Personal",
        accessorKey: "typePersonalReference",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Comentarios",
        accessorKey: "comments",
        enableColumnFilter: false,
        enableSorting: true,
      },
    ];

    return (
      <React.Fragment>
        {showPatrimonialPersonalForm ? (
          <>
            <h5 className="mb-3">Agregar Referencia Personal</h5>
            <form onSubmit={validation.handleSubmit}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <label>Nombres</label>
                  <input
                    type="text"
                    name="names"
                    value={validation.values.names}
                    onChange={validation.handleChange}
                    className="form-input"
                  />
                  {validation.touched.names && validation.errors.names ? (
                    <div className="text-red-500">{validation.errors.names}</div>
                  ) : null}
                </div>

                <div>
                  <label>Apellidos</label>
                  <input
                    type="text"
                    name="surnames"
                    value={validation.values.surnames}
                    onChange={validation.handleChange}
                    className="form-input"
                  />
                  {validation.touched.surnames && validation.errors.surnames ? (
                    <div className="text-red-500">{validation.errors.surnames}</div>
                  ) : null}
                </div>

                <div>
                  <label>Email</label>
                  <input
                    type="text"
                    name="email"
                    value={validation.values.email}
                    onChange={validation.handleChange}
                    className="form-input"
                  />
                  {validation.touched.email && validation.errors.email ? (
                    <div className="text-red-500">{validation.errors.email}</div>
                  ) : null}
                </div>

                <div>
                  <label>Teléfono</label>
                  <input
                    type="text"
                    name="phoneNumber"
                    value={validation.values.phoneNumber}
                    onChange={validation.handleChange}
                    className="form-input"
                  />
                  {validation.touched.phoneNumber && validation.errors.phoneNumber ? (
                    <div className="text-red-500">{validation.errors.phoneNumber}</div>
                  ) : null}
                </div>

                <div>
                  <label>Direccion</label>
                  <input
                    type="text"
                    name="address"
                    value={validation.values.address}
                    onChange={validation.handleChange}
                    className="form-input"
                  />
                  {validation.touched.address && validation.errors.address ? (
                    <div className="text-red-500">{validation.errors.address}</div>
                  ) : null}
                </div>

                <div>
                  <label>Tipo de Referencia</label>
                  <Select
                    options={personalReferenceOptions}
                    value={personalReferenceOptions.find(
                      (opt) => opt.value === validation.values.typePersonalReference
                    )}
                    onChange={(selected) => validation.setFieldValue("typePersonalReference", selected?.value)}
                  />
                  {validation.touched.typePersonalReference && validation.errors.typePersonalReference ? (
                    <div className="text-red-500">{validation.errors.typePersonalReference}</div>
                  ) : null}
                </div>

                <div className="md:col-span-2">
                  <label
                    htmlFor="comments"
                    className="inline-block mb-2 text-base font-medium"
                  >
                    Comentarios
                  </label>
                  <textarea
                    name="comments"
                    className="form-textarea w-full border border-slate-300 dark:border-zink-500 rounded-md focus:outline-none focus:ring-2 focus:ring-custom-500 focus:border-custom-500 dark:focus:ring-custom-800 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                    value={validation.values.comments}
                    onChange={validation.handleChange}
                    onBlur={validation.handleBlur}
                  />
                  {validation.touched.comments && validation.errors.comments ? (
                    <div className="text-red-500">{validation.errors.comments}</div>
                  ) : null}
                </div>
              </div>

              <div className="mt-4 flex justify-end gap-2">
                <button type="button" onClick={handleBackToList} className="btn bg-red-500 text-white">
                  Cancelar
                </button>
                <button type="submit" className="btn bg-blue-500 text-white">
                  Guardar
                </button>
              </div>
            </form>
          </>
        ) : (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">Referencias Personales</h2>
              <button
                className="btn bg-blue-500 text-white mt-4"
                onClick={handleAddNewPatrimonialPersonal}
              >
                Agregar Referencia Personal
              </button>
            </div>

            {patrimonialPersonalList.length === 0 ? (
              <p className="text-gray-500">No se han agregado referencias personales aún.</p>
            ) : (
              <TableContainer
                isPagination={true}
                isGlobalFilter={false}
                columns={columns}
                data={patrimonialPersonalList}
                customPageSize={5}
                divclassName="my-2 col-span-12 overflow-x-auto lg:col-span-12"
                tableclassName="hover group dataTable w-full text-sm align-middle whitespace-nowrap no-footer"
                theadclassName="border-b border-slate-200 dark:border-zink-500"
                trclassName="group-[.stripe]:even:bg-slate-50 group-[.stripe]:dark:even:bg-zink-600 transition-all duration-150 ease-linear group-[.hover]:hover:bg-slate-50 dark:group-[.hover]:hover:bg-zink-600 [&.selected]:bg-custom-500 dark:[&.selected]:bg-custom-500 [&.selected]:text-custom-50 dark:[&.selected]:text-custom-50"
                thclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500 sorting px-3 py-4 text-slate-900 bg-slate-200/50 font-semibold text-left dark:text-zink-50 dark:bg-zink-600 dark:group-[.bordered]:border-zink-500"
                tdclassName="p-3 group-[.bordered]:border group-[.bordered]:border-slate-200 group-[.bordered]:dark:border-zink-500"
                PaginationClassName="flex flex-col items-center mt-5 md:flex-row"
              />
          )}
              </div>
        )}
      </React.Fragment>
    );
  }
);

export default PatrimonialPersonalReference;
